import { useState } from 'react';
import { Button, Badge, Alert, Drawer } from '@dreamtree-org/twreact-ui';
import { PageHeader, SectionCard, StatusBadge, QueryState, EmptyState, KeyValue, StatCard, Money } from '@/components';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ReasonDialog from '../components/ReasonDialog';
import Trail from '../components/Trail';
import { useQueue } from '../hooks/useQueue';
import { filterRows, optionsWithCounts } from '../lib/filters';
import { availableActions } from '../lib/decisions';
import { slaState, hoursBetween, formatHours } from '../lib/metrics';
import { formatDateTime, humanize } from '../lib/format';

const SLA_BADGE = { breached: ['error', 'SLA breached'], due_soon: ['warning', 'Due soon'], on_track: ['success', 'On track'], closed: ['default', 'Closed'] };
const PRIORITY = { high: 'error', medium: 'warning', low: 'default' };

/**
 * ADMIN-03 Order exception desk (real since D8a): raised by store flows and the sweep —
 * return disputes, payment mismatches, return SLAs, stuck refunds. Resolutions run in the store's DB.
 */
export default function Exceptions() {
  const { query, body, rows, mutate } = useQueue('platformExceptions');
  const [q, setQ] = useState('');
  const [f, setF] = useState({ kind: 'all', status: 'open_any', sla: 'all' });
  const [openId, setOpenId] = useState(null);
  const [action, setAction] = useState(null);
  const [msg, setMsg] = useState(null);

  return (
    <div className="space-y-6">
      <PageHeader title="Order exception desk" subtitle="ADMIN-03 · return disputes, payment mismatches, return SLAs and stuck refunds" />
      <QueryState query={query}>
        {() => {
          const now = body.as_of;
          const withSla = rows.map((r) => ({ ...r, sla: r.status === 'resolved' ? 'closed' : slaState(r.due_at, now) }));
          const active = withSla.filter((r) => r.status !== 'resolved');
          const scoped = f.status === 'open_any' ? active : filterRows(withSla, { status: f.status });
          const list = filterRows(scoped, { kind: f.kind, sla: f.sla }, q, ['title', 'order_ref', 'merchant', 'customer', 'id', 'assignee'])
            .sort((a, b) => a.due_at.localeCompare(b.due_at));
          const current = withSla.find((r) => r.id === openId);
          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Open exceptions" value={active.length} />
                <StatCard label="SLA breached" value={active.filter((r) => r.sla === 'breached').length} hint="oldest first below" />
                <StatCard label="Due within 4h" value={active.filter((r) => r.sla === 'due_soon').length} />
                <StatCard label="Unassigned" value={active.filter((r) => !r.assignee).length} />
              </div>
              {msg ? <Alert variant="success" title={msg} dismissible onDismiss={() => setMsg(null)} /> : null}
              <SectionCard title="Queue (by due time)">
                <FilterBar
                  q={q}
                  onQ={setQ}
                  placeholder="Order, merchant, customer, assignee…"
                  filters={[
                    { key: 'kind', label: 'Type', value: f.kind, options: optionsWithCounts(withSla, 'kind', (k) => (body.kinds[k] ? body.kinds[k].label : k)) },
                    { key: 'status', label: 'Status', value: f.status, options: [{ value: 'open_any', label: `Not resolved (${active.length})` }, ...optionsWithCounts(withSla, 'status')] },
                    { key: 'sla', label: 'SLA', value: f.sla, options: optionsWithCounts(withSla, 'sla', (k) => SLA_BADGE[k][1]) },
                  ]}
                  onFilter={(k, v) => setF((x) => ({ ...x, [k]: v }))}
                />
                {list.length ? (
                  <DataTable
                    data={list}
                    onRowClick={(r) => setOpenId(r.id)}
                    columns={[
                      { key: 'title', label: 'Exception', render: (r) => (
                        <div className="min-w-0">
                          <div className="font-medium">{r.title}</div>
                          <div className="text-xs opacity-60">{r.id} · {r.kind_label} · {r.order_ref} · {r.merchant}</div>
                        </div>
                      ) },
                      { key: 'priority', label: 'Priority', render: (r) => <Badge variant={PRIORITY[r.priority]} size="sm">{r.priority}</Badge> },
                      { key: 'sla', label: 'SLA', render: (r) => (
                        <div>
                          <Badge variant={SLA_BADGE[r.sla][0]} size="sm">{SLA_BADGE[r.sla][1]}</Badge>
                          {r.sla !== 'closed' ? <div className="text-xs opacity-60">{formatHours(hoursBetween(now, r.due_at))} left</div> : null}
                        </div>
                      ) },
                      { key: 'assignee', label: 'Owner', render: (r) => r.assignee || <span className="opacity-60">Unassigned</span> },
                      { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                    ]}
                  />
                ) : <EmptyState title="No exceptions match">The desk is clear for this filter.</EmptyState>}
              </SectionCard>

              <Drawer
                isOpen={!!current}
                onClose={() => setOpenId(null)}
                title={current ? `${current.id} · ${current.kind_label}` : ''}
                description={current ? current.title : ''}
                size="md"
                footer={current ? (
                  <div className="flex flex-wrap justify-end gap-2">
                    {availableActions('exceptions', current).map((a) => (
                      <Button key={a.key} variant={a.variant} onClick={() => setAction(a)}>{current.assignee && a.key === 'assign' ? 'Reassign' : a.label}</Button>
                    ))}
                  </div>
                ) : null}
              >
                {current ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      <StatusBadge status={current.status} />
                      <Badge variant={SLA_BADGE[current.sla][0]}>{SLA_BADGE[current.sla][1]}</Badge>
                      <Badge variant={PRIORITY[current.priority]}>{current.priority} priority</Badge>
                    </div>
                    <KeyValue items={[
                      { label: 'Order', value: current.order_ref },
                      { label: 'Merchant', value: current.merchant },
                      { label: 'Customer', value: current.customer },
                      { label: 'Amount', value: <Money paise={current.amount_paise} /> },
                      { label: 'Opened', value: formatDateTime(current.opened_at) },
                      { label: 'Due', value: formatDateTime(current.due_at) },
                      { label: 'Owner', value: current.assignee || 'Unassigned' },
                    ]} />
                    {current.status !== 'resolved' && current.resolutions.length ? (
                      <p className="text-xs opacity-70">Resolve by: {current.resolutions.map((r) => r.label).join(' · ')}</p>
                    ) : null}
                    {current.resolution ? (
                      <Alert variant="success" title={`Resolved: ${(current.resolutions.find((r) => r.key === current.resolution) || { label: current.resolution }).label}`}>{current.resolution_note}</Alert>
                    ) : null}
                    {current.custody.length ? (
                      <div className="space-y-2">
                        <h3 className="font-semibold text-sm">Custody events</h3>
                        <Trail entries={current.custody.map((c) => ({ at: c.at, actor: 'courier / customer', action: c.event }))} />
                      </div>
                    ) : null}
                    <div className="space-y-2">
                      <h3 className="font-semibold text-sm">Decision trail</h3>
                      <Trail entries={current.trail} />
                    </div>
                  </div>
                ) : null}
              </Drawer>

              {current ? (
                <ReasonDialog
                  action={action}
                  subject={current.id}
                  assignees={action && action.key === 'assign' ? body.assignees : undefined}
                  choices={action && action.key === 'resolve' ? { label: 'How is it resolved?', options: current.resolutions.map((r) => ({ value: r.key, label: r.label })) } : undefined}
                  reasonHint={action && action.key === 'resolve' ? 'Refunds and releases run immediately in the store; the reason is kept in the trail.' : undefined}
                  onClose={() => setAction(null)}
                  onConfirm={async ({ reason, assignee, choice }) => {
                    const row = await mutate('platformExceptionDecide', { id: current.id, action: action.key, reason, assignee, resolution: choice });
                    setMsg(`${row.id} ${humanize(row.status)}${row.assignee ? ` · ${row.assignee}` : ''}`);
                  }}
                />
              ) : null}
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
